import {
	createContext,
	useContext,
	useState,
	useCallback,
	type ReactNode,
} from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';

type ModalVariant = 'success' | 'error' | 'warning' | 'danger' | 'info';
type ModalType = 'alert' | 'confirm';

interface VariantStyle {
	icon: typeof CheckCircle;
	iconBg: string;
	iconColor: string;
	confirmBtn: string;
}

interface ModalState {
	type: ModalType;
	message: string;
	variant: ModalVariant;
	title: string | null;
	confirmLabel?: string;
	cancelLabel?: string;
	resolve: (value: boolean) => void;
}

interface ModalContextValue {
	showAlert: (
		message: string,
		variant?: ModalVariant,
		title?: string | null,
	) => Promise<boolean>;
	showConfirm: (
		message: string,
		variant?: ModalVariant,
		title?: string | null,
		confirmLabel?: string,
	) => Promise<boolean>;
}

const ModalContext = createContext<ModalContextValue | null>(null);

const VARIANT_STYLES: Record<ModalVariant, VariantStyle> = {
	success: {
		icon: CheckCircle,
		iconBg: 'bg-status-success-bg',
		iconColor: 'text-status-success',
		confirmBtn: 'bg-status-success text-text-inverse hover:opacity-90',
	},
	error: {
		icon: XCircle,
		iconBg: 'bg-status-error-bg',
		iconColor: 'text-status-error',
		confirmBtn: 'bg-status-error text-text-inverse hover:opacity-90',
	},
	warning: {
		icon: AlertTriangle,
		iconBg: 'bg-status-warning-bg',
		iconColor: 'text-status-warning',
		confirmBtn: 'bg-brand-orange text-text-inverse hover:opacity-90',
	},
	danger: {
		icon: AlertTriangle,
		iconBg: 'bg-status-error-bg',
		iconColor: 'text-status-error',
		confirmBtn: 'bg-status-error text-text-inverse hover:opacity-90',
	},
	info: {
		icon: Info,
		iconBg: 'bg-status-info-bg',
		iconColor: 'text-status-info',
		confirmBtn: 'bg-brand-orange text-text-inverse hover:opacity-90',
	},
};

interface AppModalProps {
	modal: ModalState | null;
	onResolve: (value: boolean) => void;
}

function AppModal({ modal, onResolve }: AppModalProps) {
	if (!modal) return null;

	const {
		type,
		title,
		message,
		variant = 'info',
		confirmLabel = 'Confirm',
	} = modal;
	const cancelLabel = modal.cancelLabel ?? 'Cancel';
	const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.info;
	const Icon = styles.icon;

	// Wrap in a Radix Dialog so that Radix's internal focus-lock and
	// pointer-event manager recognises this as a higher-priority layer than
	// any open Sheet/Dialog behind it (e.g. a side panel). Without this,
	// Radix keeps the focus trap on the Sheet even though the modal renders
	// at a higher z-index, making the modal buttons unreachable.
	return (
		<RadixDialog.Root open modal>
			<RadixDialog.Portal>
				<RadixDialog.Overlay
					className='fixed inset-0 z-[300] bg-brand-navy/50'
					onClick={() => {
						if (type === 'alert') onResolve(true);
					}}
				/>
				<RadixDialog.Content
					className='fixed inset-0 z-[300] flex items-center justify-center p-4 pointer-events-none'
					// Prevent Radix from auto-focusing the first focusable element on open
					onOpenAutoFocus={(e) => e.preventDefault()}>
					<RadixDialog.Title className='sr-only'>
						{title || (type === 'confirm' ? 'Are you sure?' : 'Notice')}
					</RadixDialog.Title>
					<div className='bg-surface-card rounded-card shadow-modal border border-surface-border w-full max-w-md animate-in fade-in zoom-in-95 duration-150 pointer-events-auto max-h-[85vh] flex flex-col'>
						{/* Header */}
						<div className='flex items-start justify-between p-6 pb-4 flex-shrink-0'>
							<div className='flex items-center gap-3'>
								<div
									className={`w-10 h-10 rounded-card flex items-center justify-center flex-shrink-0 ${styles.iconBg}`}>
									<Icon size={20} className={styles.iconColor} />
								</div>
								<h2 className='text-heading-3 text-text-primary'>
									{title || (type === 'confirm' ? 'Are you sure?' : 'Notice')}
								</h2>
							</div>
							{type === 'alert' && (
								<button
									onClick={() => onResolve(true)}
									className='text-text-muted hover:text-text-primary transition-colors ml-4 flex-shrink-0'>
									<X size={18} />
								</button>
							)}
						</div>

						{/* Body — the only scrolling region, so a long message can never
						    push the confirm/cancel buttons out of reach. */}
						<div className='px-6 pb-6 overflow-y-auto'>
							<p className='text-body text-text-secondary whitespace-pre-wrap'>
								{message}
							</p>
						</div>

						{/* Footer */}
						<div className='flex gap-3 justify-end px-6 pb-6 border-t border-surface-border pt-4 flex-shrink-0'>
							{type === 'confirm' && (
								<button
									onClick={() => onResolve(false)}
									className='px-5 py-2.5 rounded-pill text-body-sm font-medium border border-surface-border text-text-secondary hover:bg-surface transition-colors'>
									{cancelLabel}
								</button>
							)}
							<button
								onClick={() => onResolve(true)}
								className={`px-5 py-2.5 rounded-pill text-body-sm font-medium transition-opacity ${type === 'alert' ? 'bg-brand-orange text-text-inverse hover:opacity-90' : styles.confirmBtn}`}>
								{type === 'alert' ? 'OK' : confirmLabel}
							</button>
						</div>
					</div>
				</RadixDialog.Content>
			</RadixDialog.Portal>
		</RadixDialog.Root>
	);
}

interface ModalProviderProps {
	children: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
	const [modal, setModal] = useState<ModalState | null>(null);

	const showAlert = useCallback(
		(
			message: string,
			variant: ModalVariant = 'info',
			title: string | null = null,
		): Promise<boolean> => {
			return new Promise((resolve) => {
				setModal({ type: 'alert', message, variant, title, resolve });
			});
		},
		[],
	);

	const showConfirm = useCallback(
		(
			message: string,
			variant: ModalVariant = 'warning',
			title: string | null = null,
			confirmLabel = 'Confirm',
		): Promise<boolean> => {
			return new Promise((resolve) => {
				setModal({
					type: 'confirm',
					message,
					variant,
					title,
					confirmLabel,
					resolve,
				});
			});
		},
		[],
	);

	const handleResolve = useCallback((value: boolean) => {
		setModal((prev) => {
			if (prev?.resolve) prev.resolve(value);
			return null;
		});
	}, []);

	return (
		<ModalContext.Provider value={{ showAlert, showConfirm }}>
			{children}
			<AppModal modal={modal} onResolve={handleResolve} />
		</ModalContext.Provider>
	);
}

export function useModal(): ModalContextValue {
	const ctx = useContext(ModalContext);
	if (!ctx) throw new Error('useModal must be used within ModalProvider');
	return ctx;
}
